<template>
  <section class="lead-dashboard" aria-label="Štatistiky klientov">
    <div class="dashboard-heading">
      <div>
        <span class="dashboard-eyebrow">Diagnostika leadov</span>
        <h2>Čo sa deje s klientmi</h2>
        <p>Všetci klienti vo výbere vrátane organiky a vlastných kontaktov. Výsledky jednotlivých kampaní sú v prehľade Kampane a kontakty.</p>
      </div>
      <span v-if="analytics" class="dashboard-freshness">
        Aktualizované {{ formattedFreshness }}
      </span>
    </div>

    <div v-if="loading && !analytics" class="dashboard-loading">
      <span class="dashboard-spinner"></span>
      Načítavam štatistiky…
    </div>

    <div v-else-if="error && !analytics" class="dashboard-error">
      {{ error }}
    </div>

    <template v-else-if="analytics">
      <div class="metric-grid">
        <article
          v-for="metric in metricCards"
          :key="metric.key"
          class="metric-card"
          :class="`metric-card--${metric.tone}`"
        >
          <div class="metric-topline">
            <span>{{ metric.label }}</span>
            <span
              v-if="metric.delta !== null"
              class="metric-delta"
              :class="metric.deltaClass"
            >
              {{ metric.delta >= 0 ? '↗' : '↘' }} {{ formatSigned(metric.delta) }}%
            </span>
          </div>
          <strong>{{ metric.formattedValue }}</strong>
          <small>{{ metric.note }}</small>
        </article>
      </div>

      <div class="dashboard-grid dashboard-grid--main">
        <article class="dashboard-panel funnel-panel">
          <div class="panel-heading">
            <div>
              <span class="panel-kicker">Konverzný funnel</span>
              <h3>Kam sa leady dostali</h3>
            </div>
            <span class="panel-note">z vybraného obdobia</span>
          </div>

          <div class="funnel-list">
            <div v-for="(step, index) in analytics.funnel" :key="step.key" class="funnel-row">
              <div class="funnel-copy">
                <span>{{ step.label }}</span>
                <strong>{{ step.count }}</strong>
              </div>
              <div class="funnel-track" aria-hidden="true">
                <span :style="{width: `${funnelWidth(step)}%`}"></span>
              </div>
              <div class="funnel-meta">
                <strong>{{ formatPercent(step.conversion) }}</strong>
                <small v-if="index > 0">−{{ funnelDropoff(index) }} leadov</small>
              </div>
            </div>
          </div>
        </article>

        <article class="dashboard-panel stage-panel">
          <div class="panel-heading">
            <div>
              <span class="panel-kicker">Úzke miesta</span>
              <h3>Kde sa leady zdržujú</h3>
            </div>
            <span class="panel-note">aktuálny stav</span>
          </div>

          <div v-if="stageRows.length" class="stage-list">
            <div v-for="stage in stageRows" :key="stage.key" class="stage-row">
              <div class="stage-copy">
                <strong>{{ stage.label }}</strong>
                <span>{{ stage.count }} {{ clientLabel(stage.count) }}</span>
              </div>
              <div class="stage-days">
                <strong>{{ formatDays(stage.average_days) }}</strong>
                <span>priemer v stave</span>
              </div>
              <div class="stage-risk" :class="{'has-risk': stage.stalled_count > 0}">
                <strong>{{ stage.stalled_count }}</strong>
                <span>nad {{ analytics.summary.stalled_after_days }} dní</span>
              </div>
            </div>
          </div>
          <div v-else class="panel-empty">Vo vybranom období nie sú aktívne leady.</div>
        </article>
      </div>

      <div class="dashboard-grid dashboard-grid--secondary">
        <article class="dashboard-panel archive-panel">
          <div class="panel-heading">
            <div>
              <span class="panel-kicker">Straty</span>
              <h3>Prečo leady končia v archíve</h3>
            </div>
            <span class="panel-note">{{ analytics.summary.archived }} spolu</span>
          </div>

          <div v-if="archiveTotal" class="archive-groups">
            <div v-for="group in analytics.archive_groups" :key="group.key" class="archive-group">
              <div class="archive-copy">
                <span>{{ group.label }}</span>
                <strong>{{ group.count }}</strong>
              </div>
              <div class="archive-track" aria-hidden="true">
                <span :style="{width: `${archiveShare(group.count)}%`}"></span>
              </div>
              <span class="archive-share">{{ formatPercent(archiveShare(group.count)) }}</span>
            </div>
          </div>
          <div v-else class="panel-empty">Žiadne archivované leady vo vybranom období.</div>

          <div v-if="otherArchiveDetails.length" class="archive-details">
            <span class="archive-details-title">Detail iného dôvodu</span>
            <span v-for="reason in otherArchiveDetails" :key="reason.key" class="archive-detail-chip">
              {{ reason.label }} · {{ reason.count }}
            </span>
          </div>
        </article>

        <article class="dashboard-panel status-panel">
          <div class="panel-heading">
            <div>
              <span class="panel-kicker">Pipeline</span>
              <h3>Aktuálne rozloženie</h3>
            </div>
          </div>
          <div class="status-grid">
            <div v-for="status in statusCards" :key="status.key" class="status-card">
              <span>{{ status.label }}</span>
              <strong>{{ status.value }}</strong>
            </div>
          </div>
          <p class="dashboard-caveat">
            Čas v stave sa počíta od poslednej zmeny flow. Historická cesta medzi všetkými stavmi sa zatiaľ neukladá.
          </p>
        </article>
      </div>
    </template>
  </section>
</template>

<script>
import {mapGetters} from "vuex";

const DIRECT_ARCHIVE_KEYS = new Set([
  "service_not_needed",
  "only_information",
  "brand_distrust",
]);

export default {
  name: "KlientiStats",
  computed: {
    ...mapGetters({
      analytics: "clientState/getClientAnalytics",
      previousAnalytics: "clientState/getPreviousClientAnalytics",
      loading: "clientState/getClientAnalyticsLoading",
      error: "clientState/getClientAnalyticsError",
    }),
    formattedFreshness() {
      if (!this.analytics?.generated_at) return "";
      return new Date(this.analytics.generated_at).toLocaleTimeString("sk-SK", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    metricCards() {
      const summary = this.analytics.summary;
      return [
        this.metric("total", "Leady", summary.total, "v zvolenom období", "primary"),
        this.metric("contact_rate", "Úspešne kontaktované", summary.contact_rate, `${summary.contacted} leadov`, "success", true),
        this.metric("signed_rate", "Žiadosti podpísané", summary.signed_rate, `${summary.signed} leadov`, "success", true),
        this.metric("stalled", "Zaseknuté leady", summary.stalled, `viac než ${summary.stalled_after_days} dní`, "warning", false, "", true),
        this.metric("archive_rate", "Miera archivácie", summary.archive_rate, `${summary.archived} leadov`, "danger", true, "", true),
        this.metric("average_stage_days", "Priemer v stave", summary.average_stage_days, "dní bez zmeny", "neutral", false, " d", true),
      ];
    },
    stageRows() {
      return [...this.analytics.stage_health]
        .filter(stage => stage.count > 0)
        .sort((a, b) => (
          b.stalled_count - a.stalled_count ||
          b.average_days - a.average_days ||
          b.count - a.count
        ))
        .slice(0, 7);
    },
    archiveTotal() {
      return this.analytics.archive_groups.reduce((sum, group) => sum + group.count, 0);
    },
    otherArchiveDetails() {
      return this.analytics.archive_details.filter(reason => !DIRECT_ARCHIVE_KEYS.has(reason.key));
    },
    statusCards() {
      const status = this.analytics.high_level;
      return [
        {key: "new", label: "Noví", value: status.new},
        {key: "missed", label: "Nedovolané", value: status.missed},
        {key: "approval", label: "Rozpracované", value: status.approval},
        {key: "waiting", label: "Čakačka", value: status.waiting},
        {key: "archived", label: "Archív", value: status.archived},
      ];
    },
  },
  methods: {
    metric(key, label, value, note, tone, percent = false, suffix = "", lowerIsBetter = false) {
      const previousValue = this.previousAnalytics?.summary?.[key];
      let delta = null;
      if (typeof previousValue === "number" && previousValue !== 0) {
        delta = Number((((value - previousValue) / Math.abs(previousValue)) * 100).toFixed(1));
      }
      return {
        key,
        label,
        value,
        note,
        tone,
        delta,
        deltaClass: delta === 0
          ? "is-neutral"
          : ((lowerIsBetter ? delta < 0 : delta > 0) ? "is-positive" : "is-negative"),
        formattedValue: percent ? this.formatPercent(value) : `${this.formatNumber(value)}${suffix}`,
      };
    },
    formatNumber(value) {
      return new Intl.NumberFormat("sk-SK", {maximumFractionDigits: 1}).format(value || 0);
    },
    formatPercent(value) {
      return `${this.formatNumber(value)} %`;
    },
    formatSigned(value) {
      return new Intl.NumberFormat("sk-SK", {maximumFractionDigits: 1}).format(Math.abs(value));
    },
    formatDays(value) {
      return `${this.formatNumber(value)} d`;
    },
    funnelWidth(step) {
      return step.count ? Math.max(3, step.conversion) : 0;
    },
    funnelDropoff(index) {
      const previous = this.analytics.funnel[index - 1]?.count || 0;
      return Math.max(0, previous - this.analytics.funnel[index].count);
    },
    archiveShare(count) {
      return this.archiveTotal ? Number(((count / this.archiveTotal) * 100).toFixed(1)) : 0;
    },
    clientLabel(count) {
      return count === 1 ? "klient" : "klientov";
    },
  },
};
</script>

<style scoped lang="scss">
.lead-dashboard {
  width: 100%;
  margin-top: 20px;
  padding: 2px 24px 8px 0;
  color: var(--app-text);
}

.dashboard-heading,
.panel-heading,
.metric-topline,
.funnel-copy,
.archive-copy {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.dashboard-heading {
  margin-bottom: 14px;
  color: #fff;
}

.dashboard-eyebrow,
.panel-kicker,
.identity-kicker {
  font-size: 9px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.dashboard-heading h2,
.panel-heading h3 {
  margin: 2px 0 0;
  font-weight: 800;
}

.dashboard-heading h2 {
  color: #fff;
  font-size: 19px;
}

.dashboard-freshness,
.panel-note {
  font-size: 10px;
  opacity: 0.74;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(130px, 1fr));
  gap: 10px;
}

.metric-card,
.dashboard-panel {
  border: 1px solid var(--app-border);
  background: var(--app-surface);
  color: var(--app-text);
  box-shadow: 0 10px 28px rgba(8, 27, 76, 0.12);
}

.metric-card {
  min-height: 112px;
  padding: 14px;
  border-radius: 14px;
}

.metric-card::before {
  display: block;
  width: 28px;
  height: 3px;
  margin-bottom: 12px;
  border-radius: 999px;
  background: var(--metric-accent, var(--app-primary));
  content: "";
}

.metric-card--success { --metric-accent: #38b878; }
.metric-card--warning { --metric-accent: #f2a93b; }
.metric-card--danger { --metric-accent: #ef6a70; }
.metric-card--neutral { --metric-accent: #8ca1bd; }

.metric-topline > span:first-child {
  color: var(--app-text-muted);
  font-size: 10px;
  font-weight: 700;
}

.metric-card > strong {
  display: block;
  margin-top: 7px;
  font-size: 24px;
  line-height: 1;
}

.metric-card > small {
  display: block;
  margin-top: 7px;
  color: var(--app-text-muted);
  font-size: 9px;
}

.metric-delta {
  padding: 3px 6px;
  border-radius: 999px;
  font-size: 8px;
  font-weight: 800;
}

.metric-delta.is-positive {
  background: rgba(56, 184, 120, 0.13);
  color: #38b878;
}

.metric-delta.is-negative {
  background: rgba(239, 106, 112, 0.13);
  color: #ef6a70;
}

.metric-delta.is-neutral {
  background: var(--app-surface-muted);
  color: var(--app-text-muted);
}

.dashboard-grid {
  display: grid;
  gap: 12px;
  margin-top: 12px;
}

.dashboard-grid--main {
  grid-template-columns: minmax(0, 1.1fr) minmax(360px, 0.9fr);
}

.dashboard-grid--secondary {
  grid-template-columns: minmax(0, 1.15fr) minmax(320px, 0.85fr);
}

.dashboard-panel {
  min-width: 0;
  padding: 17px;
  border-radius: 15px;
}

.panel-heading {
  margin-bottom: 14px;
}

.panel-kicker {
  color: var(--app-primary);
}

.panel-heading h3 {
  font-size: 14px;
}

.panel-note {
  color: var(--app-text-muted);
}

.funnel-list,
.stage-list,
.archive-groups {
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.funnel-row {
  display: grid;
  grid-template-columns: minmax(125px, 0.7fr) minmax(150px, 1.3fr) 76px;
  align-items: center;
  gap: 12px;
}

.funnel-copy span,
.archive-copy span,
.status-card span {
  color: var(--app-text-muted);
  font-size: 9px;
  font-weight: 600;
}

.funnel-copy strong,
.archive-copy strong {
  font-size: 11px;
}

.funnel-track,
.archive-track {
  height: 7px;
  overflow: hidden;
  border-radius: 999px;
  background: var(--app-surface-strong);
}

.funnel-track span,
.archive-track span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, var(--app-primary), #62a9ed);
}

.funnel-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.funnel-meta strong {
  font-size: 10px;
}

.funnel-meta small {
  color: var(--app-text-muted);
  font-size: 8px;
}

.stage-row {
  display: grid;
  grid-template-columns: minmax(130px, 1fr) 90px 88px;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid var(--app-border);
  border-radius: 10px;
  background: var(--app-surface-muted);
}

.stage-copy,
.stage-days,
.stage-risk {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
}

.stage-copy strong {
  overflow: hidden;
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stage-copy span,
.stage-days span,
.stage-risk span {
  color: var(--app-text-muted);
  font-size: 8px;
}

.stage-days,
.stage-risk {
  align-items: flex-end;
}

.stage-days strong,
.stage-risk strong {
  font-size: 11px;
}

.stage-risk.has-risk strong {
  color: #ef6a70;
}

.archive-group {
  display: grid;
  grid-template-columns: minmax(170px, 1fr) minmax(100px, 1fr) 45px;
  align-items: center;
  gap: 10px;
}

.archive-track span {
  background: linear-gradient(90deg, #ef6a70, #f2a26b);
}

.archive-share {
  color: var(--app-text-muted);
  font-size: 9px;
  text-align: right;
}

.archive-details {
  display: flex;
  margin-top: 14px;
  padding-top: 12px;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  border-top: 1px solid var(--app-border);
}

.archive-details-title {
  margin-right: 4px;
  color: var(--app-text-muted);
  font-size: 8px;
  font-weight: 800;
  text-transform: uppercase;
}

.archive-detail-chip {
  padding: 4px 7px;
  border-radius: 999px;
  background: var(--app-surface-muted);
  color: var(--app-text-muted);
  font-size: 8px;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
}

.status-card {
  display: flex;
  min-height: 66px;
  padding: 10px;
  flex-direction: column;
  justify-content: space-between;
  border: 1px solid var(--app-border);
  border-radius: 11px;
  background: var(--app-surface-muted);
}

.status-card strong {
  font-size: 19px;
}

.dashboard-caveat {
  margin: 14px 0 0;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--app-primary-soft);
  color: var(--app-text-muted);
  font-size: 9px;
  line-height: 1.5;
}

.dashboard-loading,
.dashboard-error,
.panel-empty {
  display: flex;
  min-height: 90px;
  align-items: center;
  justify-content: center;
  color: var(--app-text-muted);
  font-size: 11px;
}

.dashboard-loading,
.dashboard-error {
  border-radius: 14px;
  background: var(--app-surface);
}

.dashboard-spinner {
  width: 17px;
  height: 17px;
  margin-right: 8px;
  border: 2px solid var(--app-border);
  border-top-color: var(--app-primary);
  border-radius: 50%;
  animation: dashboard-spin 0.75s linear infinite;
}

@keyframes dashboard-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 1280px) {
  .metric-grid {
    grid-template-columns: repeat(3, minmax(150px, 1fr));
  }

  .dashboard-grid--main,
  .dashboard-grid--secondary {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .lead-dashboard {
    padding-right: 0;
  }

  .metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .funnel-row {
    grid-template-columns: 1fr 65px;
  }

  .funnel-track {
    grid-column: 1 / -1;
    grid-row: 2;
  }

  .stage-row {
    grid-template-columns: 1fr 70px 70px;
  }

  .archive-group {
    grid-template-columns: 1fr 40px;
  }

  .archive-track {
    grid-column: 1 / -1;
  }

  .status-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
